<template>
  <BaseTableColumn header="ID">
    <template #default="{ rowData }">
      <router-link
        :to="{ name: 'Products/Car', params: { carId: rowData.id } }"
        class="link"
      >
        {{ rowData.id }}
      </router-link>
    </template>
  </BaseTableColumn>

  <BaseTableColumn
    v-if="hasBrand"
    field="brand.name"
    label="Brand"
    is-toggable
  >
    <template #header>
      <TableHeaderWithIcon>
        <TableBoxSvg /> Brand
      </TableHeaderWithIcon>
    </template>
  </BaseTableColumn>

  <BaseTableColumn
    field="model"
    label="Model"
    is-toggable
  >
    <template #header>
      <TableHeaderWithIcon>
        <TableSubSvg /> Model
      </TableHeaderWithIcon>
    </template>
  </BaseTableColumn>

  <BaseTableColumn
    field="project_code"
    label="Code"
    is-toggable
  >
    <template #header>
      <TableHeaderWithIcon>
        <TableJigsawSvg /> Code
      </TableHeaderWithIcon>
    </template>
  </BaseTableColumn>

  <BaseTableColumn
    label="Segment"
    is-toggable
  >
    <template #header>
      <TableHeaderWithIcon>
        <TableSegmentSvg /> Segment
      </TableHeaderWithIcon>
    </template>

    <template #default="{ rowData }">
      <BaseLabel color="barley">
        {{ rowData.segment }}
      </BaseLabel>
    </template>
  </BaseTableColumn>

  <BaseTableColumn
    label="Body"
    is-toggable
  >
    <template #header>
      <TableHeaderWithIcon>
        <TableTypeSvg /> Body
      </TableHeaderWithIcon>
    </template>

    <template #default="{ rowData }">
      <BaseLabel color="violet">
        {{ rowData.body }}
      </BaseLabel>
    </template>
  </BaseTableColumn>

  <BaseTableColumn
    label="Gen Phase"
    is-toggable
  >
    <template #header>
      <TableHeaderWithIcon>
        <TablePhaseSvg /> Gen Phase
      </TableHeaderWithIcon>
    </template>

    <template #default="{ rowData }">
      <BaseLabel color="lime">
        Gen {{ rowData.generation }}
      </BaseLabel>
      <BaseLabel
        :class="$style.phaseLabel"
        color="orange"
      >
        Phase {{ rowData.phase }}
      </BaseLabel>
    </template>
  </BaseTableColumn>

  <BaseTableColumn
    label="Production"
    is-toggable
  >
    <template #header>
      <TableHeaderWithIcon>
        <TableWrenchSvg /> Production
      </TableHeaderWithIcon>
    </template>

    <template #default="{ rowData }">
      <BaseLabel color="purple">
        {{ rowData.production_type }}
      </BaseLabel>
    </template>
  </BaseTableColumn>

  <BaseTableColumn
    field="commercial_name"
    label="Commercial Name"
    is-toggable
  >
    <template #header>
      <TableHeaderWithIcon>
        <TableCommercialSvg /> Commercial Name
      </TableHeaderWithIcon>
    </template>
  </BaseTableColumn>

  <BaseTableColumn
    label="Power Train"
    is-toggable
  >
    <template #header>
      <TableHeaderWithIcon>
        <TablePowertrainSvg /> Power Train
      </TableHeaderWithIcon>
    </template>

    <template #default="{ rowData }">
      <BaseLabel color="aqua">
        {{ rowData.power_train }}
      </BaseLabel>
    </template>
  </BaseTableColumn>

  <BaseTableColumn
    label="SOP"
    is-toggable
  >
    <template #header>
      <TableHeaderWithIcon>
        <TableDateStartSvg /> SOP
      </TableHeaderWithIcon>
    </template>

    <template #default="{ rowData }">
      {{ format(new Date(rowData.sop), 'd MMMM y') }}
    </template>
  </BaseTableColumn>

  <BaseTableColumn
    label="EOP"
    is-toggable
  >
    <template #header>
      <TableHeaderWithIcon>
        <TableDateEndSvg /> EOP
      </TableHeaderWithIcon>
    </template>

    <template #default="{ rowData }">
      {{ format(new Date(rowData.eop), 'd MMMM y') }}
    </template>
  </BaseTableColumn>

  <BaseTableActionsColumn #="{ rowData }">
    <BaseDropdownActionsList>
      <router-link
        #="{ navigate }"
        :to="{ name: 'Products/Car', params: { carId: rowData.id } }"
        custom
      >
        <BaseDropdownAction @click="navigate">
          Car details
        </BaseDropdownAction>
      </router-link>
      <router-link
        #="{ navigate }"
        :to="{ name: 'Products/Car/Visualise', params: { carId: rowData.id } }"
        custom
      >
        <BaseDropdownAction @click="navigate">
          Visualise assembly
        </BaseDropdownAction>
      </router-link>
    </BaseDropdownActionsList>
  </BaseTableActionsColumn>
</template>

<script>
import { format } from 'date-fns';
import { TableActionsColumn as BaseTableActionsColumn, TableColumn as BaseTableColumn } from '@/components/base/Table';
import {
  DropdownAction as BaseDropdownAction,
  DropdownActionsList as BaseDropdownActionsList,
} from '@/components/base/Dropdown';
import TableHeaderWithIcon from '@/components/TableHeaderWithIcon';
import BaseLabel from '@/components/base/Label';
import TableBoxSvg from '@/assets/images/icons/table-box.svg?inline';
import TableSubSvg from '@/assets/images/icons/table-sub.svg?inline';
import TableJigsawSvg from '@/assets/images/icons/table-jigsaw.svg?inline';
import TableSegmentSvg from '@/assets/images/icons/table-segment.svg?inline';
import TableTypeSvg from '@/assets/images/icons/table-type.svg?inline';
import TablePhaseSvg from '@/assets/images/icons/table-phase.svg?inline';
import TableWrenchSvg from '@/assets/images/icons/table-wrench.svg?inline';
import TableCommercialSvg from '@/assets/images/icons/table-commercial.svg?inline';
import TablePowertrainSvg from '@/assets/images/icons/table-powertrain.svg?inline';
import TableDateStartSvg from '@/assets/images/icons/table-date-start.svg?inline';
import TableDateEndSvg from '@/assets/images/icons/table-date-end.svg?inline';

export default {
  components: {
    BaseTableColumn,
    TableHeaderWithIcon,
    BaseLabel,
    BaseTableActionsColumn,
    BaseDropdownActionsList,
    BaseDropdownAction,
    TableBoxSvg,
    TableSubSvg,
    TableJigsawSvg,
    TableSegmentSvg,
    TableTypeSvg,
    TablePhaseSvg,
    TableWrenchSvg,
    TableCommercialSvg,
    TablePowertrainSvg,
    TableDateStartSvg,
    TableDateEndSvg,
  },
  props: {
    hasBrand: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    format,
  },
};
</script>

<style lang="scss" module>
.phaseLabel {
  margin-left: 10px;
}
</style>
